package com.exura.pages.planCreator.components

import androidx.compose.runtime.*
import com.exura.components.Icon
import com.exura.features.plandefinition.domain.model.series.SeriesType
import com.exura.pages.planCreator.model.SeriesTypeInfo
import com.exura.pages.planCreator.model.SeriesTypeRegistry
import com.exura.ui.*
import com.exura.ui.modifiers.ShadeBorder
import com.exura.ui.modifiers.insetHoverableItemStyle
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*


val COLLAPSED_SIZE = 20.px * 2 + 12.px * 2 + 24.px + 4.px

@Composable
fun SeriesTypeSelectorPanel(
    selectedSeriesType: SeriesType? = null,
    modifier: Modifier = Modifier,
    onSeriesTypeSelected: (SeriesTypeInfo) -> Unit,
    onArrow: () -> Unit,
    isExpanded: Boolean = false,
) {
    val availableTypes = SeriesTypeRegistry.getAllAvailableTypes()

    Column(
        modifier = modifier
            .width(if(isExpanded) 400.px else COLLAPSED_SIZE)
            .backgroundColor(ExuraColors.WHITE)
            .borderRadius(12.px)
            .boxShadow(
                BoxShadow.of(
                    offsetX = (-8).px,
                    offsetY = 4.px,
                    blurRadius = 24.px,
                    spreadRadius = 0.px,
                    color = ExuraColors.BLACK.copy(alpha = 0.15).rgba
                )
            )
            .border {
                width(Dimens.separatorWidthDefault)
                style(LineStyle.Solid)
                color(ExuraColors.LIGHT_GRAY.copy(alpha = 0.3))
            }
            .padding(20.px)
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .margin(bottom = 20.px),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Box(
                modifier = Modifier
                    .insetHoverableItemStyle(
                        isSelected = false,
                        shadeSize = 4.px,
                        shadeBorders = listOf(
                            ShadeBorder.TOP,
                            ShadeBorder.LEFT,
                        ),
                        cursor = Cursor.Pointer,
                        onClick = onArrow,
                    )
                    .padding(12.px)
                    .borderRadius(8.px)
                    .textAlign(TextAlign.Center),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    iconSrc = if(isExpanded) Assets.IconsUntiled.PROCEED
                    else Assets.IconsUntiled.REVERT,
                    size = 24.px,
                    color = ExuraColors.ARSENIC2,
                )
            }

            if (isExpanded) {
                SpanText(
                    text = "Dodaj nową serię",
                    modifier = Modifier
                        .classNames(TextStyles.medium20)
                        .color(ExuraColors.ARSENIC)
                )
            }
        }

        // Series type options
        Column(
            verticalArrangement = Arrangement.spacedBy(4.px)
        ) {
            availableTypes.forEachIndexed { index, seriesType ->
                SeriesTypeOption(
                    seriesType = seriesType,
                    isExpanded = isExpanded,
                    isSelected = selectedSeriesType == seriesType.seriesType,
                    onClick = { onSeriesTypeSelected(seriesType) },
                )
            }
        }
    }
}

@Composable
private fun ColumnScope.SeriesTypeOption(
    seriesType: SeriesTypeInfo,
    onClick: () -> Unit,
    isExpanded: Boolean = true,
    isSelected: Boolean = false,
) {

    val newMod = Modifier
        .insetHoverableItemStyle(
            isSelected = isSelected,
            shadeSize = if (isExpanded) 12.px else 8.px,
            shadeBorders = listOf(
                ShadeBorder.TOP,
                ShadeBorder.LEFT,
            ),
            cursor = Cursor.Pointer,
            onClick = onClick,
        )
    Row(
        modifier = Modifier
            .weight(1f)
            .fillMaxWidth()
            .borderRadius(8.px)
            .padding(12.px)
            .then(newMod),
        horizontalArrangement = Arrangement.spacedBy(16.px),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Row(
            horizontalArrangement = Arrangement.spacedBy(8.px),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            seriesType.icon()
            if (isExpanded) {
                Icon(
                    iconSrc = Assets.IconsUntiled.PROCEED,
                    size = 18.px,
                )
                seriesType.spawnedIcon()
            }
        }

        if(isExpanded) {
            // Series type info
            Column(
                modifier = Modifier.weight(1f),
                verticalArrangement = Arrangement.spacedBy(4.px)
            ) {
                SpanText(
                    text = seriesType.name,
                    modifier = Modifier
                        .classNames(TextStyles.medium16)
                        .color(ExuraColors.ARSENIC)
                )

                SpanText(
                    text = seriesType.prompt,
                    modifier = Modifier
                        .classNames(TextStyles.normal14)
                        .color(ExuraColors.ARSENIC2)
                        .lineHeight(1.3)
                )
            }
        }
    }
}

/**
 * Container that positions the series selector panel relative to canvas area only
 */
@Composable
fun SeriesSelectorPanelContainer(
    isVisible: Boolean,
    onSeriesTypeSelected: (SeriesTypeInfo) -> Unit,
    onClose: () -> Unit,
    canvasModifier: Modifier = Modifier
) {
    if (isVisible) {
        Box(
            modifier = canvasModifier.position(Position.Relative)
        ) {
            // Overlay background (only covers canvas)
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .backgroundColor(ExuraColors.BLACK.copy(alpha = 0.3))
                    .cursor(Cursor.Pointer)
                    .onClick { onClose() }
            )

            // Series selector panel positioned from right
            Box(
                modifier = Modifier
                    .position(Position.Absolute)
                    .top(20.px)
                    .right(20.px)
                    .zIndex(1000)
            ) {
                SeriesTypeSelectorPanel(
                    onSeriesTypeSelected = onSeriesTypeSelected,
                    onArrow = onClose
                )
            }
        }
    }
}
